import config from '@/config'
import { useGlobalStore } from '@/store/app'
import { GAEventTypes, trackCustomGAEvents } from '@ghl/clientportal-core'
import { requests } from './HTTPService'
import { ServiceWithStoreDependencies } from './decorators/serviceDecorators'

const http = requests(config?.COMMUNITIES_SERVER_URL)

@ServiceWithStoreDependencies('groupId')
class EventsServiceCls {
  constructor() {}

  get endpoint(): string {
    const appStore = useGlobalStore()
    return `/${appStore.locationId}/groups/${appStore.groupId}/events`
  }

  async fetchEvents(startDate: string, endDate: string): Promise<any> {
    const response = await http.get(
      `${this.endpoint}?startDate=${startDate}&endDate=${endDate}`
    )
    return response.data
  }

  async fetchEventById(
    eventId: string,
    startTime: string,
    endTime: string
  ): Promise<any> {
    const response = await http.get(`${this.endpoint}/${eventId}`, {
      params: {
        startTime,
        endTime,
      },
    })
    return response.data
  }

  async fetchPastAndUpcomingEvents(query: any): Promise<any> {
    const response = await http.get(`${this.endpoint}/past-upcoming`, {
      params: query,
    })
    return response.data
  }

  async createEvent(data: any): Promise<any> {
    const response = await http.post(this.endpoint, data)
    trackCustomGAEvents(GAEventTypes.EVENT_CREATED, {
      eventType: data.isRecurring ? 'Recurring' : 'One-Time',
      locationId: useGlobalStore().locationId,
    })
    return response.data
  }

  async updateEvent(data: any): Promise<any> {
    const response = await http.patch(`${this.endpoint}/${data._id}`, data)
    return response.data
  }

  async deleteEvent(eventId: string, query?: any): Promise<any> {
    const response = await http.delete(`${this.endpoint}/${eventId}`, {
      params: query,
    })
    return response.data
  }

  async rsvpByEventId(eventId: string, data: any): Promise<any> {
    const response = await http.post(`${this.endpoint}/${eventId}/rsvp`, data)
    trackCustomGAEvents(GAEventTypes.EVENT_RSVP, {
      locationId: useGlobalStore().locationId,
    })
    return response.data
  }

  async addEventToCalendar(calendar: string, eventId: string): Promise<any> {
    const response = await http.get(
      `${this.endpoint}/${eventId}/add-to-calendar/${calendar}`
    )
    return response.data
  }

  async exportAttendees(eventId: string): Promise<any> {
    const response = await http.get(
      `${this.endpoint}/${eventId}/export-attendees`
    )
    return response.data
  }
}

// Export singleton instance
export const EventsService = new EventsServiceCls()

// Export type for dependency injection
export type IEventsService = EventsServiceCls

// Export commonly used methods directly
export const { createEvent, updateEvent, deleteEvent } = EventsService
